import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { CookieSettingsDialog } from './CookieSettingsDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'aqarnet-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'aqarnet-cookie-preferences';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export function CookieConsentBanner() {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!hasConsented) {
      // Delay banner to avoid being detected as LCP element
      // This allows main page content to be measured as LCP first
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(onlyNecessary));
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setShowBanner(false);
  };

  const handleCustomize = () => {
    setShowSettings(true);
  };

  const handleSettingsClose = (open: boolean) => {
    setShowSettings(open);
    if (!open) {
      // Check if preferences were saved
      const hasConsented = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (hasConsented) {
        setShowBanner(false);
      }
    }
  };

  const handleSettingsSaved = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setShowBanner(false);
    setShowSettings(false);
  };

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
            <div className="max-w-4xl mx-auto bg-background border border-border rounded-lg shadow-lg p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <Cookie className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h3 className="font-semibold text-foreground">
                      {t('cookie_banner.title')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('cookie_banner.description')}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRejectAll}
                    className="whitespace-nowrap"
                  >
                    {t('cookie_banner.reject_all')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCustomize}
                    className="whitespace-nowrap"
                  >
                    {t('cookie_banner.customize')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="whitespace-nowrap"
                  >
                    {t('cookie_banner.accept_all')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CookieSettingsDialog
        open={showSettings}
        onOpenChange={handleSettingsClose}
        onSave={handleSettingsSaved}
      />
    </>
  );
}
